"use client";

import { useState, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import Link from "next/link";
import {
  HiCalculator,
  HiClock,
  HiVideoCamera,
  HiVolumeUp,
  HiSave,
  HiQuestionMarkCircle,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { videoBitrateCalculatorFAQ } from "@/lib/data/faq/video-bitrate-calculator";
import { faqToFAQPageSchema } from "@/lib/schemas/faq-schemas";
import JSONLDSchema from "@/components/JSONLDSchema";

type BitrateFormValues = {
  hours: number;
  minutes: number;
  seconds: number;

  audioBitrate: number; // kbps

  // Mode specific
  targetSizeMB: number;
  videoBitrate: number; // kbps
};

export default function VideoBitrateCalculatorClient() {
  const { register, control, setValue } = useForm<BitrateFormValues>({
    defaultValues: {
      hours: 0,
      minutes: 5,
      seconds: 0,
      audioBitrate: 192,
      targetSizeMB: 25, // Discord limit
      videoBitrate: 5000,
    },
  });

  const values = useWatch({ control });
  const [mode, setMode] = useState<"size" | "bitrate">("bitrate"); // Calculate Bitrate (from size) or Size (from bitrate)

  const { resultBitrate, resultSize } = useMemo(() => {
    const totalSeconds =
      (Number(values.hours) || 0) * 3600 +
      (Number(values.minutes) || 0) * 60 +
      (Number(values.seconds) || 0);
    const audioKbps = Number(values.audioBitrate) || 0;

    if (totalSeconds <= 0) {
      return { resultBitrate: null, resultSize: null };
    }

    if (mode === "bitrate") {
      // Calculate max video bitrate for target size
      // Size (MB) = (Bitrate (kbps) * Duration (s)) / 8192
      // Bitrate (kbps) = (Size (MB) * 8192) / Duration (s)
      const targetSizeMB = Number(values.targetSizeMB) || 0;
      const totalBitrate = (targetSizeMB * 8192) / totalSeconds;
      const maxVideoBitrate = totalBitrate - audioKbps;

      return {
        resultBitrate: maxVideoBitrate > 0 ? Math.floor(maxVideoBitrate) : 0,
        resultSize: null,
      };
    } else {
      // Calculate estimated file size
      const videoKbps = Number(values.videoBitrate) || 0;
      const totalKbps = videoKbps + audioKbps;
      const sizeMB = (totalKbps * totalSeconds) / 8192;

      return {
        resultBitrate: null,
        resultSize: Number(sizeMB.toFixed(2)),
      };
    }
  }, [values, mode]);

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Video Bitrate & File Size Calculator"
        description="Calculate optimal video bitrates and estimated file sizes for Discord, Email, or Streaming. Perfect for staying under upload limits."
        url="https://plugmonkey.xyz/tool/video-bitrate-calculator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Video Bitrate Calculator",
            url: "https://plugmonkey.xyz/tool/video-bitrate-calculator/",
          },
        ]}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiCalculator className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Video Bitrate Calculator</h1>
          <p className="text-muted-foreground mt-1">
            Calculate optimal bitrates for Discord, Email, or Streaming.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Uploading videos to Discord, email, or streaming platforms? File size limits can be
          frustrating. This calculator helps you find the perfect balance between video quality and
          file size, so you never hit upload limits again.
        </p>
        <p>
          Work in two modes: calculate the exact video bitrate needed to fit a target file size
          (perfect for Discord's 8MB or 25MB limits), or estimate your final file size from a chosen
          bitrate. The tool accounts for both video and audio bitrates, giving you accurate results
          for your specific use case.
        </p>
        <p>
          Perfect for content creators, streamers, and anyone who needs to optimize video files for
          different platforms. No more trial and error—get the right settings the first time.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="bitrate" onValueChange={(v) => setMode(v as "bitrate" | "size")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="bitrate">Calculate Bitrate (Fit to Size)</TabsTrigger>
              <TabsTrigger value="size">Calculate File Size</TabsTrigger>
            </TabsList>

            <div className="mt-6 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HiClock /> Video Duration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vbc-hours">Hours</Label>
                      <Input id="vbc-hours" type="number" min="0" {...register("hours")} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vbc-minutes">Minutes</Label>
                      <Input id="vbc-minutes" type="number" min="0" {...register("minutes")} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vbc-seconds">Seconds</Label>
                      <Input id="vbc-seconds" type="number" min="0" {...register("seconds")} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HiVolumeUp /> Audio Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label htmlFor="vbc-audioBitrate">Audio Bitrate (kbps)</Label>
                    <select
                      id="vbc-audioBitrate"
                      {...register("audioBitrate")}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="64">64 kbps (Speech)</option>
                      <option value="96">96 kbps (Low Quality)</option>
                      <option value="128">128 kbps (Standard)</option>
                      <option value="160">160 kbps (Good)</option>
                      <option value="192">192 kbps (High Quality)</option>
                      <option value="256">256 kbps (Studio)</option>
                      <option value="320">320 kbps (Lossless-like)</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              <TabsContent value="bitrate">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HiSave /> Target File Size
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="vbc-targetSizeMB">Max File Size (MB)</Label>
                        <Input
                          id="vbc-targetSizeMB"
                          type="number"
                          step="0.1"
                          {...register("targetSizeMB")}
                        />
                      </div>
                      <div className="flex gap-2 text-sm text-muted-foreground">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setValue("targetSizeMB", 8)}
                        >
                          8MB (Discord Free)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setValue("targetSizeMB", 25)}
                        >
                          25MB (Gmail/Discord)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setValue("targetSizeMB", 100)}
                        >
                          100MB
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setValue("targetSizeMB", 500)}
                        >
                          500MB (Discord Nitro)
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="size">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HiVideoCamera /> Video Bitrate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="vbc-videoBitrate">Video Bitrate (kbps)</Label>
                      <Input
                        id="vbc-videoBitrate"
                        type="number"
                        step="100"
                        {...register("videoBitrate")}
                      />
                      <p className="text-xs text-muted-foreground">
                        Typical: 2500-5000 for 1080p, 8000+ for 4K
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-primary/5 border-primary/20 shadow-lg overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-orange-500" />
              <CardHeader>
                <CardTitle>Result</CardTitle>
                <CardDescription>
                  {mode === "bitrate" ? "Required Video Bitrate" : "Estimated File Size"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  {mode === "bitrate" ? (
                    <>
                      <div className="text-5xl font-bold tracking-tighter text-primary">
                        {resultBitrate !== null ? resultBitrate : "--"}
                        <span className="text-xl text-muted-foreground font-normal ml-1">kbps</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Set this as your video bitrate in OBS, Premiere, or Handbrake.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-5xl font-bold tracking-tighter text-primary">
                        {resultSize !== null ? resultSize : "--"}
                        <span className="text-xl text-muted-foreground font-normal ml-1">MB</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Estimated final file size (Video + Audio).
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Compressing Videos?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                If you are downloading videos from the web, <strong>Video Downloader Pro</strong>{" "}
                helps you choose the right quality before downloading.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/video-downloader-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={videoBitrateCalculatorFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      {(() => {
        const faqSchema = faqToFAQPageSchema(videoBitrateCalculatorFAQ, {
          url: "https://plugmonkey.xyz/tool/video-bitrate-calculator/",
          name: "Video Bitrate Calculator - Frequently Asked Questions",
          description:
            "Get answers to common questions about calculating video bitrates and file sizes for Discord, email, and streaming.",
        });
        return faqSchema ? <JSONLDSchema schema={faqSchema} /> : null;
      })()}

      <ToolsCustomDevCTA />
    </div>
  );
}
